import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { PageHeader } from '@/components/PageHeader';
import { Card, Button, Stepper, Toggle, Chip, Select } from '@/components/ui';
import { useTrainingStore } from '@/store';
import { db } from '@/lib/db/db';
import { EliteTrainingEngine } from '@/features/training/components/EliteTrainingEngine';
import { Play } from 'lucide-react';
import { useHaptics } from '@/hooks/use-haptics';

export function TrainPage() {
    const location = useLocation();
    const presets = useLiveQuery(() => db.trainingPresets.toArray()) ?? [];

    // We only use the store for CONFIG now. 
    // The Active Engine manages its own state for performance.
    const {
        workSeconds, restSeconds, totalRounds, presetId, position, whistleStart, cueMode,
        configure,
    } = useTrainingStore();

    const [activeMode, setActiveMode] = useState(false);

    const { trigger, patterns } = useHaptics();

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const mode = params.get('mode');

        if (mode === 'whistle') {
            configure({
                presetId: 'whistle_starts',
                name: 'Whistle Starts',
                workSeconds: 10,
                restSeconds: 10,
                totalRounds: 10,
                whistleStart: true,
                trainMode: 'whistle_starts'
            } as any);
        }

        const navPresetId = (location.state as { presetId?: string } | null)?.presetId;
        if (navPresetId) {
            const p = presets.find(pr => pr.id === navPresetId);
            if (p) {
                configure({
                    presetId: p.id, position: p.position,
                    workSeconds: p.workSeconds, restSeconds: p.restSeconds,
                    totalRounds: p.rounds, cueMode: p.cueMode,
                });
            }
        }
    }, [location.state, location.search, presets, configure]);

    const handleStart = () => {
        trigger(patterns.medium);
        setActiveMode(true);
    };

    const positions: ('neutral' | 'top' | 'bottom')[] = ['neutral', 'top', 'bottom'];

    if (activeMode) {
        return (
            <EliteTrainingEngine
                presetName={presets.find(p => p.id === presetId)?.name ?? "Custom Session"}
                workSec={workSeconds}
                restSec={restSeconds}
                rounds={totalRounds}
                whistleStart={whistleStart}
            />
        );
    }

    const presetOptions = [
        { label: "Custom Manual Configuration", value: "" },
        ...presets.map(p => ({ label: p.name, value: p.id }))
    ];

    return (
        <div className="space-y-6 pb-24 animate-fade-in">
            <PageHeader title="Training Engine" subtitle="Configure your round timer" />

            <div className="space-y-4 px-4">
                {/* Preset Picker */}
                <Card className="py-2 px-4 shadow-none bg-transparent border-0 p-0 overflow-visible relative z-50">
                    <div className="mb-2 px-1">
                        <label className="text-[10px] font-bold text-text-muted uppercase tracking-wider block">Session Preset</label>
                    </div>
                    <Select
                        value={presetId ?? ""}
                        onChange={(val) => {
                            const p = presets.find(pr => pr.id === val);
                            if (p) {
                                configure({
                                    presetId: p.id, position: p.position,
                                    workSeconds: p.workSeconds, restSeconds: p.restSeconds,
                                    totalRounds: p.rounds, cueMode: p.cueMode,
                                });
                            } else {
                                configure({ presetId: null });
                            }
                        }}
                        options={presetOptions}
                        placeholder="Select a preset..."
                    />
                </Card>

                {/* Position */}
                <div className="flex gap-2 py-1">
                    {positions.map(pos => (
                        <Chip
                            key={pos}
                            label={pos.charAt(0).toUpperCase() + pos.slice(1)}
                            active={position === pos}
                            onClick={() => configure({ position: pos })}
                        />
                    ))}
                </div>

                {/* Steppers */}
                <Card className="space-y-2">
                    <Stepper label="Work" value={workSeconds} onChange={v => configure({ workSeconds: v })} min={5} max={300} step={5} formatValue={v => `${v}s`} />
                    <div className="border-t border-white/[0.05]" />
                    <Stepper label="Rest" value={restSeconds} onChange={v => configure({ restSeconds: v })} min={5} max={120} step={5} formatValue={v => `${v}s`} />
                    <div className="border-t border-white/[0.05]" />
                    <Stepper label="Rounds" value={totalRounds} onChange={v => configure({ totalRounds: v })} min={1} max={20} />
                </Card>

                {/* Toggles */}
                <Card className="space-y-2">
                    <Toggle label="Whistle Start" checked={whistleStart} onChange={v => configure({ whistleStart: v })} />
                    <div className="border-t border-white/[0.05]" />
                    <div className="flex items-center justify-between min-h-[44px]">
                        <span className="text-text-secondary font-medium">Cues</span>
                        <div className="flex gap-2">
                            <Chip label="Basic" active={cueMode === 'basic'} onClick={() => configure({ cueMode: 'basic' })} />
                            <Chip label="None" active={cueMode === 'none'} onClick={() => configure({ cueMode: 'none' })} />
                        </div>
                    </div>
                </Card>
            </div>

            {/* Start Button */}
            <div className="fixed bottom-[88px] inset-x-0 px-8 z-40 max-w-md mx-auto">
                <Button onClick={handleStart} size="lg" isHero className="w-full text-lg shadow-button-elite hover:scale-[1.02] active:scale-[0.98]">
                    <Play className="w-5 h-5 fill-current mr-2" /> START SESSION
                </Button>
            </div>
        </div>
    );
}
